import { useLayoutEffect, useState } from "react";

type UseOverflowOptions<T extends HTMLElement> = {
  /** 自定义溢出阈值，例如展开后仍按折叠时的行数判断。传入稳定的函数。 */
  measure?: (element: T) => boolean;
};

function hasOverflow(element: HTMLElement): boolean {
  return element.scrollHeight - element.clientHeight > 1 || element.scrollWidth - element.clientWidth > 1;
}

/** 检测元素溢出；回调 ref 会在节点被 Tooltip 等组件替换时重新绑定观察器。 */
export function useOverflow<T extends HTMLElement>({ measure = hasOverflow }: UseOverflowOptions<T> = {}) {
  const [element, ref] = useState<T | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    if (!element) {
      setIsOverflowing(false);
      return;
    }

    const update = () => setIsOverflowing(measure(element));
    update();

    const resizeObserver = new ResizeObserver(update);
    const mutationObserver = new MutationObserver(update);
    resizeObserver.observe(element);
    // 被截断元素的文字变化时，盒子尺寸可能不变，ResizeObserver 不会触发。
    mutationObserver.observe(element, { childList: true, characterData: true, subtree: true });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [element, measure]);

  return [ref, isOverflowing] as const;
}
